import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { MessageCircle } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { SITE } from "@/lib/site";

// Premium WhatsApp sticky — onyx ink + gold ring, soft shadow, animated halo,
// elegant entrance after scroll. Hidden on /success (mid-checkout) and on the
// dedicated /kennenlerngespraech page (which already has WA in its fallback strip).
const HIDE_ON = ["/success"];

const StickyWhatsApp = () => {
    const { t, lang } = useLanguage();
    const location = useLocation();
    const [shown, setShown] = useState(false);
    const [revealLabel, setRevealLabel] = useState(false);

    // Localized micro-copy
    const labels = {
        de: { tooltip: "Kurz schreiben?", aria: "WhatsApp öffnen" },
        es: { tooltip: "¿Me escribes?", aria: "Abrir WhatsApp" },
    };
    const L = labels[lang] || labels.de;

    // Show after a soft delay + small scroll, never instantly. Reveal label
    // briefly on first appearance to draw the eye, then collapse to icon-only.
    useEffect(() => {
        const t1 = setTimeout(() => setShown(true), 1200);
        const t2 = setTimeout(() => setRevealLabel(true), 1700);
        const t3 = setTimeout(() => setRevealLabel(false), 5200);
        return () => {
            clearTimeout(t1);
            clearTimeout(t2);
            clearTimeout(t3);
        };
    }, []);

    if (HIDE_ON.includes(location.pathname)) return null;
    if (!t || !SITE.contact.whatsappUrl) return null;

    return (
        <div
            data-testid="sticky-whatsapp"
            aria-hidden={!shown}
            className={`fixed z-40 bottom-6 right-6 md:bottom-8 md:right-8 transition-all duration-700 ease-out ${
                shown
                    ? "opacity-100 translate-y-0"
                    : "opacity-0 translate-y-4 pointer-events-none"
            }`}
        >
            <a
                href={SITE.contact.whatsappUrl}
                target="_blank"
                rel="noreferrer"
                aria-label={L.aria}
                data-testid="sticky-whatsapp-link"
                className="group relative inline-flex items-center gap-3 pl-3 pr-3 md:pl-4 md:pr-5 py-3 rounded-full bg-ella-ink text-ella-cream shadow-[0_14px_40px_rgba(31,29,28,0.28)] ring-1 ring-ella-gold/25 hover:ring-ella-gold/55 hover:bg-ella-charcoal transition-[box-shadow,background,transform] duration-300 hover:-translate-y-0.5 backdrop-blur"
            >
                {/* Soft animated halo */}
                <span
                    aria-hidden="true"
                    className="absolute inset-0 rounded-full bg-ella-gold/15 animate-sticky-halo pointer-events-none"
                />

                {/* Icon */}
                <span className="relative inline-flex items-center justify-center w-9 h-9 rounded-full bg-ella-gold/15 text-ella-gold transition-colors duration-300 group-hover:bg-ella-gold group-hover:text-ella-onyx">
                    <MessageCircle className="w-4 h-4" strokeWidth={1.6} />
                </span>

                {/* Label — animated reveal/collapse. Hidden on mobile entirely
                    to avoid covering Hero CTAs / content. */}
                <span
                    className={`relative font-sans text-[12px] tracking-[0.18em] uppercase whitespace-nowrap text-ella-cream/90 transition-[max-width,opacity,margin] duration-500 overflow-hidden hidden md:inline-flex ${
                        revealLabel
                            ? "md:max-w-[180px] md:opacity-100 md:ml-0"
                            : "md:max-w-[180px] md:opacity-100 md:ml-0"
                    }`}
                >
                    {L.tooltip}
                </span>
            </a>
        </div>
    );
};

export default StickyWhatsApp;
